import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  GraduationCap, Plus, Lock, X, Trash2, FileDown, ChevronDown, ChevronUp, ArrowRight, CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fortbildungen`;
const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR - 2, THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1];

export default function CampusFortbildungen() {
  const { user } = useCampusAuth();
  const allowed = user?.tier === "Hauptquartier" || user?.is_admin;
  const [year, setYear] = useState(THIS_YEAR);
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState({ themen: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [entryFor, setEntryFor] = useState(null); // employee object

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/mitarbeiter?year=${year}`, { withCredentials: true }),
        axios.get(`${API}/themen`, { withCredentials: true }),
      ]);
      setEmployees(m.data);
      setMeta(t.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Tracker konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [allowed, year]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-10" data-testid="fortbildungen-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
            Helden-Hauptquartier · Pflichtfortbildungen
          </div>
          <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
            Der Fortbildungs-Tracker.
          </h1>
          <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-2xl">
            Erfasse die Pflichtfortbildungen deines Teams (45 Min = 1 UE = 1 FP) und lade
            auf Knopfdruck den <strong className="text-sand-800">MD-/MDK-prüfungsfesten Fortbildungsnachweis als PDF</strong> herunter.
          </p>
        </div>
        {allowed ? (
          <div className="flex items-center gap-3">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="fortbildung-year-select"
              className="rounded-full border border-cream-300 bg-white px-4 h-11 text-[13.5px] font-medium text-sand-700 outline-none focus:border-sand-800"
            >
              {YEARS.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <Button
              onClick={() => setShowEmployeeForm(true)}
              data-testid="fortbildung-add-employee"
              className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 text-[13.5px] font-medium"
            >
              <span className="inline-flex items-center gap-2">
                <Plus className="h-4 w-4" strokeWidth={1.8} />
                Mitarbeiter:in anlegen
              </span>
            </Button>
          </div>
        ) : (
          <Link
            to="/campus/team"
            data-testid="fortbildung-upgrade-cta"
            className="inline-flex items-center gap-2 rounded-full border border-terra text-terra hover:bg-terra hover:text-white transition-colors h-11 px-5 text-[13.5px] font-medium"
          >
            <Lock className="h-3.5 w-3.5" strokeWidth={1.8} />
            Mit Hauptquartier-Lizenz freischalten
          </Link>
        )}
      </header>

      {!allowed ? (
        <LockedState />
      ) : loading ? (
        <div className="text-sand-500 text-[14px]">Lade Fortbildungs-Daten …</div>
      ) : employees.length === 0 ? (
        <EmptyState onNew={() => setShowEmployeeForm(true)} />
      ) : (
        <section className="grid gap-5">
          {employees.map((emp) => (
            <EmployeeCard
              key={emp.id}
              emp={emp}
              year={year}
              onLogEntry={() => setEntryFor(emp)}
              onChanged={load}
            />
          ))}
        </section>
      )}

      {showEmployeeForm && (
        <EmployeeFormModal
          roles={meta.roles || []}
          onClose={() => setShowEmployeeForm(false)}
          onCreated={() => { setShowEmployeeForm(false); load(); }}
        />
      )}
      {entryFor && (
        <EntryFormModal
          employee={entryFor}
          themen={meta.themen || []}
          year={year}
          onClose={() => setEntryFor(null)}
          onCreated={() => { setEntryFor(null); load(); }}
        />
      )}
    </div>
  );
}

function EmployeeCard({ emp, year, onLogEntry, onChanged }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const pct = Math.min(100, Math.round((emp.total_ue / (emp.target_ue || 16)) * 100));
  const done = emp.total_ue >= (emp.target_ue || 16);

  const loadEntries = async () => {
    try {
      const res = await axios.get(`${API}/mitarbeiter/${emp.id}/eintraege?year=${year}`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Einträge konnten nicht geladen werden.");
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadEntries();
  };

  const downloadCert = async () => {
    try {
      const res = await axios.get(`${API}/mitarbeiter/${emp.id}/zertifikat?year=${year}`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Fortbildungsnachweis-${emp.last_name}-${emp.first_name}-${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fortbildungsnachweis heruntergeladen.");
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error(`Für ${year} sind noch keine Fortbildungen erfasst.`);
      } else {
        toast.error("PDF konnte nicht erstellt werden.");
      }
    }
  };

  const removeEmployee = async () => {
    if (!window.confirm(`${emp.first_name} ${emp.last_name} wirklich entfernen?`)) return;
    try {
      await axios.delete(`${API}/mitarbeiter/${emp.id}`, { withCredentials: true });
      toast.success("Mitarbeiter:in entfernt.");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Entfernen fehlgeschlagen.");
    }
  };

  const removeEntry = async (eid) => {
    try {
      await axios.delete(`${API}/eintraege/${eid}`, { withCredentials: true });
      toast.success("Eintrag gelöscht.");
      loadEntries();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Löschen fehlgeschlagen.");
    }
  };

  return (
    <article data-testid={`fortbildung-employee-${emp.id}`} className="rounded-2xl border border-cream-300 bg-white p-6">
      <div className="flex items-start gap-4 flex-wrap">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-terra/10 text-terra shrink-0">
          <GraduationCap className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-display text-sand-800 text-[1.25rem] font-semibold leading-tight">
            {emp.first_name} {emp.last_name}
          </h3>
          <div className="mt-1 text-[12.5px] text-sand-600">
            {emp.role} · Soll {emp.target_ue} UE/Jahr · {emp.entries_count} {emp.entries_count === 1 ? "Eintrag" : "Einträge"} in {year}
          </div>
          {/* Fortschritt */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-cream-100 overflow-hidden max-w-sm">
              <div
                className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-terra"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span
              data-testid={`fortbildung-progress-${emp.id}`}
              className={`text-[12.5px] font-semibold ${done ? "text-emerald-600" : "text-sand-700"}`}
            >
              {emp.total_ue} / {emp.target_ue} UE · {emp.total_fp} FP
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={onLogEntry}
            data-testid={`fortbildung-log-entry-${emp.id}`}
            className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-9 px-4 text-[12.5px] font-medium"
          >
            <span className="inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" strokeWidth={1.8} />Stunden erfassen</span>
          </Button>
          <button
            onClick={downloadCert}
            data-testid={`fortbildung-cert-download-${emp.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-terra text-terra hover:bg-terra hover:text-white transition-colors h-9 px-4 text-[12.5px] font-medium"
          >
            <FileDown className="h-3.5 w-3.5" strokeWidth={1.8} />
            Zertifikat (PDF)
          </button>
          <button
            onClick={toggle}
            data-testid={`fortbildung-toggle-entries-${emp.id}`}
            className="inline-flex items-center gap-1 text-[12.5px] text-sand-600 hover:text-sand-800 h-9 px-2"
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Einträge
          </button>
          <button
            onClick={removeEmployee}
            data-testid={`fortbildung-delete-employee-${emp.id}`}
            className="text-sand-400 hover:text-terra inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-cream-100"
            aria-label="Mitarbeiter:in entfernen"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.7} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5 pt-5 border-t border-cream-200">
          {entries === null ? (
            <div className="text-sand-500 text-[13px]">Lade Einträge …</div>
          ) : entries.length === 0 ? (
            <div className="text-sand-500 text-[13px]">Noch keine Fortbildungen in {year} erfasst.</div>
          ) : (
            <ul className="grid gap-2">
              {entries.map((e) => (
                <li
                  key={e.id}
                  data-testid={`fortbildung-entry-${e.id}`}
                  className="flex items-center gap-3 rounded-xl bg-cream-50 border border-cream-200 px-4 py-2.5 text-[13.5px]"
                >
                  <CalendarDays className="h-4 w-4 text-sand-400 shrink-0" strokeWidth={1.7} />
                  <span className="text-sand-600 tabular-nums shrink-0">
                    {new Date(e.date).toLocaleDateString("de-DE")}
                  </span>
                  <span className="flex-1 text-sand-800 font-medium truncate">{e.title}</span>
                  {e.anbieter && <span className="text-sand-500 hidden sm:inline truncate max-w-[160px]">{e.anbieter}</span>}
                  <span className="text-sand-700 font-semibold shrink-0">{e.ue} UE · {e.fp} FP</span>
                  <button
                    onClick={() => removeEntry(e.id)}
                    data-testid={`fortbildung-delete-entry-${e.id}`}
                    className="text-sand-400 hover:text-terra"
                    aria-label="Eintrag löschen"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.7} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

function LockedState() {
  return (
    <div className="rounded-3xl border border-dashed border-cream-300 bg-white p-12 text-center" data-testid="fortbildung-locked">
      <Lock className="h-8 w-8 text-sand-400 mx-auto" strokeWidth={1.4} />
      <h3 className="mt-4 font-display text-sand-800 text-xl font-semibold">Hauptquartier-Funktion.</h3>
      <p className="mt-2 text-sand-600 text-[14px] max-w-md mx-auto">
        Mit der Hauptquartier-Lizenz trackst du die Pflichtfortbildungen deines gesamten Teams
        und erstellst MD-/MDK-Nachweise per Klick.
      </p>
      <Link
        to="/campus/team"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra text-white hover:bg-terra/90 transition-colors h-11 px-6 text-[13.5px] font-medium"
      >
        Hauptquartier kennenlernen
        <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
      </Link>
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="rounded-3xl border border-dashed border-cream-300 bg-white p-12 text-center" data-testid="fortbildung-empty">
      <GraduationCap className="h-8 w-8 text-sand-400 mx-auto" strokeWidth={1.4} />
      <h3 className="mt-4 font-display text-sand-800 text-xl font-semibold">Noch keine Mitarbeitenden.</h3>
      <p className="mt-2 text-sand-600 text-[14px] max-w-md mx-auto">
        Lege dein Team an, erfasse die absolvierten Pflichtfortbildungen und behalte das
        Jahres-Soll (UE/FP) jederzeit im Blick.
      </p>
      <Button onClick={onNew} data-testid="fortbildung-empty-add" className="mt-6 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5">
        <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" strokeWidth={1.8} />Erste:n Mitarbeiter:in anlegen</span>
      </Button>
    </div>
  );
}

function EmployeeFormModal({ roles, onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", role: roles[0]?.role || "Pflegefachkraft", target_ue: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const defaultTarget = roles.find((r) => r.role === form.role)?.target_ue;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await axios.post(`${API}/mitarbeiter`, {
        first_name: form.first_name,
        last_name: form.last_name,
        role: form.role,
        target_ue: form.target_ue ? Number(form.target_ue) : null,
      }, { withCredentials: true });
      toast.success("Mitarbeiter:in angelegt.");
      onCreated();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Anlegen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Mitarbeiter:in anlegen" subtitle="Stammdaten für den Fortbildungs-Tracker.">
      <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="fortbildung-employee-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Vorname" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="employee-form-firstname" required />
          <Field label="Nachname" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="employee-form-lastname" required />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <SelectField
            label="Qualifikation"
            value={form.role}
            options={roles.map((r) => r.role)}
            onChange={(v) => setForm({ ...form, role: v })}
            testid="employee-form-role"
          />
          <Field
            label={`Jahres-Soll UE (Standard: ${defaultTarget ?? 16})`}
            type="number"
            value={form.target_ue}
            onChange={(v) => setForm({ ...form, target_ue: v })}
            testid="employee-form-target"
            placeholder={`${defaultTarget ?? 16}`}
          />
        </div>
        {err && <p className="text-[13px] text-terra font-medium" data-testid="employee-form-error">{err}</p>}
        <ModalActions busy={busy} busyLabel="Wird angelegt…" label="Anlegen" onClose={onClose} testid="employee-form-submit" />
      </form>
    </Modal>
  );
}

function EntryFormModal({ employee, themen, year, onClose, onCreated }) {
  const [form, setForm] = useState({
    topic_slug: themen[0]?.slug || "hygiene",
    title: "",
    date: `${year}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`,
    ue: themen[0]?.ue || 8,
    anbieter: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const isSonstiges = form.topic_slug === "sonstiges";

  const onTopicChange = (slug) => {
    const t = themen.find((x) => x.slug === slug);
    setForm({ ...form, topic_slug: slug, ue: t?.ue || form.ue || 1 });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await axios.post(`${API}/mitarbeiter/${employee.id}/eintraege`, {
        topic_slug: form.topic_slug,
        title: form.title || null,
        date: form.date,
        ue: Number(form.ue),
        anbieter: form.anbieter || null,
      }, { withCredentials: true });
      toast.success("Fortbildung erfasst.");
      onCreated();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Erfassen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="Stunden erfassen"
      subtitle={`${employee.first_name} ${employee.last_name} · 45 Min = 1 UE = 1 FP`}
    >
      <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="fortbildung-entry-form">
        <SelectField
          label="Pflichtthema"
          value={form.topic_slug}
          options={themen.map((t) => t.slug)}
          optionLabels={themen.reduce((acc, t) => ({ ...acc, [t.slug]: t.title }), {})}
          onChange={onTopicChange}
          testid="entry-form-topic"
        />
        {isSonstiges && (
          <Field label="Titel der Fortbildung" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="entry-form-title" required />
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Datum" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="entry-form-date" required />
          <Field label="UE (à 45 Min)" type="number" value={form.ue} onChange={(v) => setForm({ ...form, ue: v })} testid="entry-form-ue" required min="0.5" step="0.5" />
        </div>
        <Field label="Anbieter (optional)" value={form.anbieter} onChange={(v) => setForm({ ...form, anbieter: v })} testid="entry-form-anbieter" placeholder="z. B. Dickies-Campus" />
        {err && <p className="text-[13px] text-terra font-medium" data-testid="entry-form-error">{err}</p>}
        <ModalActions busy={busy} busyLabel="Wird erfasst…" label="Erfassen" onClose={onClose} testid="entry-form-submit" />
      </form>
    </Modal>
  );
}

/* ---------- kleine UI-Bausteine (Stil identisch zu CampusJobs) ---------- */
function Modal({ children, onClose, title, subtitle }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-sand-800/30 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-xl rounded-2xl bg-white border border-cream-300 shadow-hover p-7 md:p-8 overflow-y-auto max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center rounded-full text-sand-500 hover:bg-cream-100"
        >
          <X className="h-4 w-4" strokeWidth={1.7} />
        </button>
        <h2 className="font-display text-sand-800 text-2xl font-semibold">{title}</h2>
        <p className="mt-1 text-sand-600 text-[13.5px]">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ busy, busyLabel, label, onClose, testid }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-2">
      <button type="button" onClick={onClose} className="text-[13.5px] text-sand-600 hover:text-sand-800 underline-offset-4 hover:underline">
        Abbrechen
      </button>
      <Button type="submit" disabled={busy} data-testid={testid} className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 disabled:opacity-60">
        <span className="inline-flex items-center gap-2">{busy ? busyLabel : label}{!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.8} />}</span>
      </Button>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, required, min, step }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">{label}</label>
      <input
        type={type} value={value} placeholder={placeholder} required={required} min={min} step={step}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800 transition-colors"
      />
    </div>
  );
}

function SelectField({ label, value, options, optionLabels, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">{label}</label>
      <select
        value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-xl border border-cream-300 bg-white px-3 h-11 text-[14.5px] outline-none focus:border-sand-800"
      >
        {options.map((o) => (<option key={o} value={o}>{optionLabels?.[o] || o}</option>))}
      </select>
    </div>
  );
}
